import React, { useState, useEffect, useRef } from "react";
import {
  Calendar,
  Users,
  MapPin,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Receipt,
  Building,
  Globe,
  Gift,
  Calculator,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import BossStatisticByDateShowDataModal from "./BossStatisticByDateShowDataModal";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../AppContext";
import { MyDomain } from "../../../App";

/* ─── Styles ─────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;700&display=swap');

  .sbd * { box-sizing: border-box; }
  .sbd {
    font-family: 'Plus Jakarta Sans', sans-serif;
    color: #111827;
    padding-bottom: 80px;
  }

  /* ── Month scroller ── */
  .sbd-month-section { margin-bottom: 16px; }
  .sbd-sec-label {
    font-size: 11px; font-weight: 700; color: #6B7280;
    letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 10px;
  }
  .sbd-month-row { display: flex; align-items: center; gap: 6px; }
  .sbd-month-arrow {
    width: 30px; height: 30px; border-radius: 8px;
    border: 1px solid #E5E7EB; background: #fff;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: #6B7280; transition: all 0.15s; flex-shrink: 0;
  }
  .sbd-month-arrow:hover { border-color: #10B981; color: #059669; background: #ECFDF5; }
  .sbd-month-scroll-wrap { flex: 1; overflow: hidden; position: relative; }
  .sbd-month-scroll-wrap::before,
  .sbd-month-scroll-wrap::after {
    content: ''; position: absolute; top: 0; bottom: 0; width: 18px; z-index: 2; pointer-events: none;
  }
  .sbd-month-scroll-wrap::before { left: 0;  background: linear-gradient(to right, #F0F2F7, transparent); }
  .sbd-month-scroll-wrap::after  { right: 0; background: linear-gradient(to left,  #F0F2F7, transparent); }
  .sbd-month-track {
    display: flex; gap: 6px; overflow-x: auto; scroll-behavior: smooth;
    padding: 2px 4px; scrollbar-width: none;
  }
  .sbd-month-track::-webkit-scrollbar { display: none; }
  .sbd-month-btn {
    flex-shrink: 0; width: 62px; padding: 7px 5px;
    border-radius: 10px; border: 1.5px solid #E5E7EB;
    background: #fff; cursor: pointer; text-align: center; transition: all 0.18s;
  }
  .sbd-month-btn:hover { border-color: #6EE7B7; background: #ECFDF5; }
  .sbd-month-btn.active {
    background: #10B981; border-color: #10B981;
    box-shadow: 0 3px 10px rgba(16,185,129,0.35); transform: scale(1.06);
  }
  .sbd-month-name { font-size: 13px; font-weight: 700; display: block; color: #374151; line-height: 1.2; }
  .sbd-month-btn.active .sbd-month-name { color: #fff; }
  .sbd-month-year { font-size: 9px; font-weight: 500; color: #9CA3AF; font-family: 'JetBrains Mono', monospace; display: block; margin-top: 2px; }
  .sbd-month-btn.active .sbd-month-year { color: rgba(255,255,255,0.75); }

  /* ── Page title ── */
  .sbd-page-title {
    font-size: 18px; font-weight: 800; color: #111827;
    letter-spacing: -0.02em; margin-bottom: 14px;
  }
  .sbd-page-title span { color: #10B981; }

  /* ── Quick stat pills ── */
  .sbd-stat-row { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin-bottom: 16px; }
  .sbd-stat-btn {
    background: #fff; border-radius: 12px; border: 1px solid #E5E7EB;
    border-bottom: 3px solid transparent;
    padding: 10px 8px; display: flex; flex-direction: column; align-items: center; gap: 4px;
    cursor: pointer; transition: all 0.15s; font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .sbd-stat-btn:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.08); transform: translateY(-1px); }
  .sbd-stat-btn.blue   { border-bottom-color: #3B82F6; }
  .sbd-stat-btn.purple { border-bottom-color: #7C3AED; }
  .sbd-stat-btn.amber  { border-bottom-color: #F59E0B; }
  .sbd-stat-ico {
    width: 30px; height: 30px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
  }
  .sbd-stat-ico.blue   { background: #EFF6FF; }
  .sbd-stat-ico.purple { background: #EDE9FE; }
  .sbd-stat-ico.amber  { background: #FEF3C7; }
  .sbd-stat-val { font-size: 18px; font-weight: 800; color: #111827; letter-spacing: -0.03em; line-height: 1; }
  .sbd-stat-lbl { font-size: 9px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.06em; }

  /* ── Section blocks ── */
  .sbd-block {
    background: #fff; border-radius: 13px; border: 1px solid #E5E7EB;
    padding: 13px 14px; margin-bottom: 10px;
  }
  .sbd-block-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #F3F4F6;
  }
  .sbd-block-header-left { display: flex; align-items: center; gap: 8px; }
  .sbd-block-title { font-size: 13px; font-weight: 700; color: #111827; }
  .sbd-block-badge {
    font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 6px;
    font-family: 'JetBrains Mono', monospace;
  }
  .sbd-block-ico {
    width: 30px; height: 30px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  /* Revenue mini cards grid */
  .sbd-rev-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px; }
  .sbd-rev-card {
    background: #F9FAFB; border-radius: 10px; border: 1px solid #E5E7EB;
    border-left: 3px solid transparent; padding: 10px 11px;
  }
  .sbd-rev-card-lbl { font-size: 10px; font-weight: 600; color: #9CA3AF; margin-bottom: 3px; display: flex; align-items: center; gap: 4px; }
  .sbd-rev-card-val { font-size: 15px; font-weight: 800; color: #111827; font-family: 'JetBrains Mono', monospace; letter-spacing: -0.01em; }
  .sbd-rev-card-sub { font-size: 9px; color: #9CA3AF; margin-top: 2px; }

  /* Total row */
  .sbd-total-row {
    display: flex; align-items: center; justify-content: space-between;
    background: #F9FAFB; border-radius: 10px; padding: 10px 12px;
  }
  .sbd-total-row-left { display: flex; align-items: center; gap: 8px; }
  .sbd-total-lbl { font-size: 11px; font-weight: 600; color: #6B7280; }
  .sbd-total-val { font-family: 'JetBrains Mono', monospace; font-size: 16px; font-weight: 800; color: #111827; }

  /* Global contract per-place */
  .sbd-contract-card {
    background: #F5F3FF; border: 1px solid #DDD6FE;
    border-radius: 10px; padding: 10px 12px; margin-bottom: 8px;
  }
  .sbd-contract-header { display: flex; align-items: center; gap: 7px; margin-bottom: 8px; }
  .sbd-contract-dot { width: 6px; height: 6px; border-radius: 50%; background: #7C3AED; flex-shrink: 0; }
  .sbd-contract-name { flex: 1; font-size: 12px; font-weight: 700; color: #4C1D95; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sbd-contract-badge { font-size: 9px; font-weight: 700; background: #DDD6FE; color: #5B21B6; padding: 2px 7px; border-radius: 5px; font-family: 'JetBrains Mono', monospace; }
  .sbd-contract-amounts { display: flex; align-items: baseline; gap: 5px; margin-bottom: 6px; }
  .sbd-contract-recv { font-size: 14px; font-weight: 800; color: #7C3AED; font-family: 'JetBrains Mono', monospace; }
  .sbd-contract-slash { color: #C4B5FD; font-size: 12px; }
  .sbd-contract-total { font-size: 12px; font-weight: 600; color: #A78BFA; font-family: 'JetBrains Mono', monospace; }
  .sbd-contract-pct { margin-left: auto; font-size: 10px; font-weight: 700; background: #EDE9FE; color: #5B21B6; padding: 2px 6px; border-radius: 5px; }
  .sbd-progress { height: 5px; background: #EDE9FE; border-radius: 3px; overflow: hidden; margin-bottom: 7px; }
  .sbd-progress-fill { height: 100%; background: #7C3AED; border-radius: 3px; transition: width 0.4s; }
  .sbd-contract-payments { padding-left: 13px; display: flex; flex-direction: column; gap: 3px; }
  .sbd-payment-row { display: flex; align-items: center; justify-content: space-between; }
  .sbd-payment-date { font-size: 10px; color: #7C3AED; font-weight: 500; display: flex; align-items: center; gap: 4px; }
  .sbd-payment-amt  { font-size: 10px; font-weight: 700; color: #5B21B6; font-family: 'JetBrains Mono', monospace; }
  .sbd-status-chip {
    display: flex; align-items: center; gap: 5px;
    border-radius: 7px; padding: 4px 8px; margin-top: 6px;
    font-size: 10px; font-weight: 700;
  }
  .sbd-status-chip.ok  { background: #D1FAE5; color: #059669; }
  .sbd-status-chip.bad { background: #FEE2E2; color: #DC2626; }

  /* Extra profit per-place */
  .sbd-extra-card {
    background: #FFFBEB; border: 1px solid #FDE68A;
    border-radius: 10px; padding: 10px 12px; margin-bottom: 8px;
  }
  .sbd-extra-header { display: flex; align-items: center; gap: 7px; margin-bottom: 7px; }
  .sbd-extra-dot { width: 6px; height: 6px; border-radius: 50%; background: #F59E0B; flex-shrink: 0; }
  .sbd-extra-name { flex: 1; font-size: 12px; font-weight: 700; color: #92400E; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sbd-extra-total { font-size: 12px; font-weight: 800; color: #D97706; font-family: 'JetBrains Mono', monospace; }
  .sbd-extra-row { display: flex; align-items: center; justify-content: space-between; padding: 3px 0; border-bottom: 1px solid #FEF3C7; }
  .sbd-extra-row:last-child { border-bottom: none; }
  .sbd-extra-left { display: flex; align-items: center; gap: 5px; flex: 1; min-width: 0; }
  .sbd-extra-date { font-size: 10px; font-weight: 600; color: #D97706; }
  .sbd-extra-note { font-size: 9px; color: #92400E; font-style: italic; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sbd-extra-amt { font-size: 11px; font-weight: 700; color: #B45309; font-family: 'JetBrains Mono', monospace; flex-shrink: 0; }

  /* Operating costs */
  .sbd-cost-row {
    display: flex; align-items: center; gap: 10px;
    background: #F9FAFB; border: 1px solid #E5E7EB;
    border-radius: 10px; padding: 10px 12px; margin-bottom: 8px;
  }
  .sbd-cost-ico { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .sbd-cost-lbl { flex: 1; font-size: 12px; font-weight: 600; color: #374151; }
  .sbd-cost-val { font-family: 'JetBrains Mono', monospace; font-size: 14px; font-weight: 800; }

  /* Invoice list inside cost block */
  .sbd-inv-item {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 8px; padding: 7px 0; border-bottom: 1px solid #F3F4F6;
  }
  .sbd-inv-item:last-child { border-bottom: none; }
  .sbd-inv-name { font-size: 11px; font-weight: 600; color: #374151; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
  .sbd-inv-date { font-size: 9px; color: #9CA3AF; margin-top: 1px; }
  .sbd-inv-rate { font-size: 9px; color: #7c3aed; font-style: italic; margin-top: 1px; }
  .sbd-inv-right { text-align: right; flex-shrink: 0; }
  .sbd-inv-orig { font-size: 11px; font-weight: 700; color: #7c3aed; font-family: monospace; }
  .sbd-inv-ils  { font-size: 10px; color: #6d28d9; font-family: monospace; margin-top: 1px; }
  .sbd-inv-fail { font-size: 9px; color: #f59e0b; margin-top: 1px; }

  /* Total summary */
  .sbd-summary-block {
    background: #fff; border-radius: 13px; border: 2px solid #E5E7EB;
    padding: 14px; margin-bottom: 10px;
  }
  .sbd-summary-title {
    display: flex; align-items: center; gap: 7px;
    font-size: 13px; font-weight: 700; color: #111827;
    padding-bottom: 10px; margin-bottom: 12px; border-bottom: 1px solid #F3F4F6;
  }
  .sbd-breakdown-box {
    background: #F9FAFB; border-radius: 10px; padding: 11px 12px; margin-bottom: 12px;
  }
  .sbd-breakdown-total {
    display: flex; align-items: baseline; justify-content: space-between;
    padding-bottom: 8px; margin-bottom: 8px; border-bottom: 1px solid #E5E7EB;
  }
  .sbd-breakdown-total-lbl { font-size: 11px; font-weight: 600; color: #6B7280; }
  .sbd-breakdown-total-val { font-family: 'JetBrains Mono', monospace; font-size: 20px; font-weight: 800; color: #111827; }
  .sbd-breakdown-line { display: flex; align-items: center; justify-content: space-between; margin-bottom: 5px; }
  .sbd-breakdown-line-lbl { font-size: 11px; color: #6B7280; }
  .sbd-breakdown-line-val { font-size: 12px; font-weight: 700; color: #374151; font-family: 'JetBrains Mono', monospace; }

  /* Net profit card */
  .sbd-net {
    border-radius: 12px; padding: 14px 16px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .sbd-net.positive { background: #F0FDF4; border: 2px solid #86EFAC; }
  .sbd-net.negative { background: #FFF1F2; border: 2px solid #FECACA; }
  .sbd-net-lbl { font-size: 11px; font-weight: 600; color: #6B7280; margin-bottom: 3px; }
  .sbd-net-val { font-family: 'JetBrains Mono', monospace; font-size: 26px; font-weight: 800; letter-spacing: -0.03em; }
  .sbd-net.positive .sbd-net-val { color: #059669; }
  .sbd-net.negative .sbd-net-val { color: #DC2626; }
  .sbd-net-ico { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
  .sbd-net.positive .sbd-net-ico { background: #D1FAE5; }
  .sbd-net.negative .sbd-net-ico { background: #FEE2E2; }

  /* ── Daily breakdown grid ── */
  .sbd-daily-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
  .sbd-daily-badge {
    font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600;
    color: #059669; background: #D1FAE5; border: 1px solid #6EE7B7;
    border-radius: 6px; padding: 3px 8px;
  }

  .sbd-day-grid {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;
  }
  @media (min-width: 640px) { .sbd-day-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (min-width: 900px) { .sbd-day-grid { grid-template-columns: repeat(4, 1fr); } }

  .sbd-day {
    background: #fff; border: 1.5px solid #E5E7EB;
    border-radius: 12px; padding: 10px;
    cursor: pointer; position: relative;
    transition: all 0.18s ease; display: flex; flex-direction: column; text-align: left;
  }
  .sbd-day:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.09); transform: translateY(-2px); }
  .sbd-day.active { border-color: #6EE7B7; background: #F0FDF4; }
  .sbd-day.today  { box-shadow: 0 0 0 2px rgba(59,130,246,0.22); border-color: #93C5FD; }

  .sbd-day-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
  .sbd-day-num { font-size: 18px; font-weight: 800; color: #D1D5DB; line-height: 1; }
  .sbd-day.active .sbd-day-num { color: #059669; }
  .sbd-day.today  .sbd-day-num { color: #3B82F6; }
  .sbd-day-name { font-size: 8px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.07em; }
  .sbd-day.active .sbd-day-name { color: #6B7280; }
  .sbd-day-note-dot {
    position: absolute; top: 6px; right: 6px;
    width: 14px; height: 14px; border-radius: 4px;
    background: #FEF9C3; border: 1px solid #FDE68A;
    display: flex; align-items: center; justify-content: center;
  }

  .sbd-day-badges { display: flex; flex-wrap: wrap; gap: 3px; margin-bottom: 7px; }
  .sbd-chip { display: inline-flex; align-items: center; gap: 3px; font-size: 9px; font-weight: 700; padding: 2px 5px; border-radius: 4px; }
  .sbd-chip.emp  { background: #EDE9FE; color: #7C3AED; }
  .sbd-chip.map  { background: #FEF3C7; color: #D97706; }
  .sbd-chip.exp  { background: #FEE2E2; color: #DC2626; }

  .sbd-day-places { display: flex; flex-direction: column; gap: 5px; }
  .sbd-place-row { background: #F9FAFB; border-radius: 8px; padding: 6px 8px; }
  .sbd-day.active .sbd-place-row { background: #ECFDF5; }
  .sbd-place-name-row { display: flex; align-items: center; gap: 5px; margin-bottom: 3px; padding-bottom: 3px; border-bottom: 1px solid #E5E7EB; }
  .sbd-day.active .sbd-place-name-row { border-bottom-color: #D1FAE5; }
  .sbd-place-dot { width: 5px; height: 5px; border-radius: 50%; background: #10B981; flex-shrink: 0; }
  .sbd-place-name-txt { font-size: 9px; font-weight: 700; color: #374151; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sbd-place-emp-count { font-size: 8px; font-weight: 700; background: #D1FAE5; color: #059669; border-radius: 3px; padding: 1px 4px; }
  .sbd-emp-line { display: flex; align-items: center; justify-content: space-between; padding: 1px 0; }
  .sbd-emp-line-name { font-size: 9px; color: #6B7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
  .sbd-emp-line-sal { font-size: 9px; font-weight: 700; color: #DC2626; font-family: 'JetBrains Mono', monospace; flex-shrink: 0; }
  .sbd-place-total-row { display: flex; justify-content: space-between; align-items: center; margin-top: 3px; padding-top: 3px; border-top: 1px solid #E5E7EB; }
  .sbd-day.active .sbd-place-total-row { border-top-color: #D1FAE5; }
  .sbd-place-total-lbl { font-size: 8px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; }
  .sbd-place-total-val { font-size: 10px; font-weight: 800; color: #111827; font-family: 'JetBrains Mono', monospace; }

  .sbd-day-totals {
    background: #fff; border: 1px solid #E5E7EB; border-radius: 8px; padding: 6px 8px; margin-top: 5px;
  }
  .sbd-day.active .sbd-day-totals { background: #ECFDF5; border-color: #D1FAE5; }
  .sbd-day-total-line { display: flex; align-items: center; justify-content: space-between; }
  .sbd-day-total-lbl { font-size: 9px; font-weight: 600; color: #6B7280; display: flex; align-items: center; gap: 3px; }
  .sbd-day-total-val { font-size: 10px; font-weight: 800; color: #111827; font-family: 'JetBrains Mono', monospace; }

  .sbd-day-empty { flex: 1; display: flex; align-items: center; justify-content: center; padding: 12px 0; }
  .sbd-day-empty span { font-size: 10px; color: #9CA3AF; font-weight: 500; background: #F9FAFB; padding: 4px 10px; border-radius: 6px; }

  /* Loading */
  .sbd-loading { display: flex; align-items: center; justify-content: center; height: 200px; }
  .sbd-spinner { width: 32px; height: 32px; border: 3px solid #E5E7EB; border-top-color: #10B981; border-radius: 50%; animation: sbd-spin 0.75s linear infinite; }
  @keyframes sbd-spin { to { transform: rotate(360deg); } }

  .sbd-hide-scroll::-webkit-scrollbar { display: none; }
  .sbd-hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }

  /* ── Info tooltip ── */
  .sbd-tooltip-wrap { position: relative; display: inline-flex; align-items: center; margin-left: 4px; vertical-align: middle; }
  .sbd-info-btn {
    width: 14px; height: 14px; border-radius: 50%;
    background: #E5E7EB; color: #6B7280;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 9px; font-weight: 800; cursor: default;
    border: 1px solid #D1D5DB; padding: 0; line-height: 1; flex-shrink: 0;
    font-style: italic; font-family: Georgia, serif;
    transition: background 0.15s, color 0.15s;
    user-select: none;
  }
  .sbd-info-btn:hover { background: #C7D2FE; color: #4338CA; border-color: #A5B4FC; }
  .sbd-tooltip-box {
    position: absolute; bottom: calc(100% + 7px); left: 50%;
    transform: translateX(-50%);
    background: #1F2937; color: #F9FAFB;
    font-size: 10px; font-weight: 500; line-height: 1.45;
    padding: 7px 10px; border-radius: 8px;
    width: 190px; text-align: center;
    pointer-events: none; opacity: 0;
    transition: opacity 0.18s;
    z-index: 200; white-space: normal;
    box-shadow: 0 4px 14px rgba(0,0,0,0.28);
    font-style: normal; font-family: 'Plus Jakarta Sans', sans-serif;
  }
  .sbd-tooltip-box::after {
    content: ''; position: absolute; top: 100%; left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent; border-top-color: #1F2937;
  }
  .sbd-tooltip-wrap:hover .sbd-tooltip-box { opacity: 1; }
`;

// ── Currency symbol map ──────────────────────────────────────────────────────
const CURRENCY_SYM = { ILS: "₪", USD: "$", EUR: "€", GBP: "£" };

const InfoTooltip = ({ text }) => (
  <span className="sbd-tooltip-wrap">
    <span className="sbd-info-btn">i</span>
    <span className="sbd-tooltip-box">{text}</span>
  </span>
);

export default function BossStatisticByDate() {
  const { t } = useTranslation();
  const { AllData: contextAllData, User: contextUser } = useAppContext();
  const history = useHistory();
  const monthTrackRef = useRef(null);

  const [allData, setAllData] = useState(null);
  const [choosedMonth, setChoosedMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalData, setModalData] = useState([]);

  // ── Invoices enriched with ILS equivalent ───────────────────────────────
  const [enrichedInvoices, setEnrichedInvoices] = useState([]);

  useEffect(() => {
    const data = localStorage.getItem("allData");
    const month =
      localStorage.getItem("choosedMonth") || moment().format("MM-YYYY");
    if (data) setAllData(JSON.parse(data));
    setChoosedMonth(month);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (choosedMonth) localStorage.setItem("choosedMonth", choosedMonth);
  }, [choosedMonth]);

  // Sync allData whenever the shared context is updated (e.g. after link/unlink invoice)
  useEffect(() => {
    if (contextAllData) {
      setAllData(contextAllData);
      localStorage.setItem("allData", JSON.stringify(contextAllData));
    }
  }, [contextAllData]);

  useEffect(() => {
    if (!choosedMonth || !monthTrackRef.current) return;
    const idx = monthList.findIndex((m) => m === choosedMonth);
    if (idx < 0) return;
    const btn = monthTrackRef.current.children[idx];
    if (btn)
      btn.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
  }, [choosedMonth]);

  // ── Fetch ILS rate for any currency ─────────────────────────────────────
  const getExchangeRate = async (currency) => {
    try {
      if (currency === "ILS") return 1;
      const res = await fetch(`https://open.er-api.com/v6/latest/${currency}`);
      if (res.ok) {
        const d = await res.json();
        if (d?.rates?.ILS && d.rates.ILS > 0) return d.rates.ILS;
      }
      // fallback
      const res2 = await fetch(
        `https://api.exchangerate.host/convert?from=${currency}&to=ILS&amount=1`,
      );
      if (res2.ok) {
        const d2 = await res2.json();
        if (d2?.result && d2.result > 0) return d2.result;
      }
      return null;
    } catch {
      return null;
    }
  };

  // Fetch invoices directly from the API so placeId is always up-to-date.
  // contextUser is available immediately (from AppContext useState, not a useEffect).
  // Re-runs on mount and whenever contextAllData changes (link/unlink in Places2).
  useEffect(() => {
    const userId = contextUser?.id;
    if (!userId) return;
    const API = MyDomain.endsWith("/") ? MyDomain.slice(0, -1) : MyDomain;
    (async () => {
      try {
        const res = await fetch(`${API}/api/email-invoices/${userId}`);
        if (!res.ok) return;
        const d = await res.json();
        const invoices = d.invoices || [];
        const enriched = await Promise.all(
          invoices.map(async (inv) => {
            const currency = inv.currency || "ILS";
            if (currency === "ILS")
              return {
                ...inv,
                ilsEquivalent: parseFloat(inv.price || 0),
                exchangeRate: 1,
              };
            const rate = await getExchangeRate(currency);
            const ilsEquivalent =
              rate != null ? parseFloat(inv.price || 0) * rate : null;
            return { ...inv, ilsEquivalent, exchangeRate: rate };
          }),
        );
        setEnrichedInvoices(enriched);
      } catch {}
    })();
  }, [contextUser?.id, contextAllData]);

  if (loading || !allData) {
    return (
      <div className="sbd">
        <style>{STYLES}</style>
        <div className="sbd-loading">
          <div className="sbd-spinner" />
        </div>
      </div>
    );
  }

  const generateMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(
        `${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`,
      );
    }
    return months;
  };
  const monthList = generateMonths();

  const scrollMonths = (dir) => {
    if (monthTrackRef.current)
      monthTrackRef.current.scrollBy({ left: dir * 180, behavior: "smooth" });
  };

  const getAllDaysInMonth = () => {
    if (!choosedMonth) return [];
    const [month, year] = choosedMonth.split("-");
    const days = moment(`${year}-${month}`, "YYYY-MM").daysInMonth();
    return Array.from(
      { length: days },
      (_, i) => `${String(i + 1).padStart(2, "0")}-${month}-${year}`,
    );
  };

  const groupByDate = () => {
    const g = {};
    (allData.myDailyWork || []).forEach((item) => {
      if (!item.date) return;
      const [, month, year] = item.date.split("-");
      if (`${month}-${year}` === choosedMonth) {
        if (!g[item.date]) g[item.date] = [];
        g[item.date].push(item);
      }
    });
    return g;
  };

  const groupExpensesByDate = () => {
    const g = {};
    if (!Array.isArray(allData.myExpenses)) return g;
    allData.myExpenses.forEach((exp) => {
      if (!exp.date) return;
      const parts = exp.date.split("-");
      if (parts.length !== 3) return;
      const [, month, year] = parts;
      if (`${month}-${year}` === choosedMonth) {
        if (!g[exp.date]) g[exp.date] = [];
        g[exp.date].push(exp);
      }
    });
    return g;
  };

  const allDays = getAllDaysInMonth();
  const groupedData = groupByDate();
  const groupedExpenses = groupExpensesByDate();

  // ── Filter invoices for the selected month (linked only) ───────────────
  // Invoice date format from backend: dd/MM/yyyy
  const monthInvoices = enrichedInvoices.filter((inv) => {
    if (!inv.date) return false;
    if (!inv.placeId) return false; // only linked invoices
    const sep = inv.date.includes("/") ? "/" : "-";
    const parts = inv.date.split(sep);
    if (parts.length !== 3) return false;
    // parts[0]=day, parts[1]=month, parts[2]=year  (dd/MM/yyyy)
    const mm = parts[1].padStart(2, "0");
    const yyyy = parts[2];
    return `${mm}-${yyyy}` === choosedMonth;
  });

  // Total invoice cost in ILS for the selected month
  const totalInvoicesCost = monthInvoices.reduce((s, inv) => {
    const ils =
      inv.ilsEquivalent != null
        ? inv.ilsEquivalent
        : parseFloat(inv.price || 0);
    return s + ils;
  }, 0);

  const getDateWorkByPlace = (date) => {
    const dayData = groupedData[date] || [];
    const pg = {};
    dayData.forEach((work) => {
      if (!pg[work.placeId]) {
        const place = (allData.myPlaces || []).find(
          (p) => p.id === work.placeId,
        );
        pg[work.placeId] = {
          placeId: work.placeId,
          placeName: place?.place || "Unknown",
          employees: [],
          totalSalary: 0,
        };
      }
      const emp = (allData.myEmployees || []).find(
        (e) => e.id === work.employeeId,
      );
      const salary = parseFloat(work.salary) || 0;
      pg[work.placeId].employees.push({ name: emp?.name || "Unknown", salary });
      pg[work.placeId].totalSalary += salary;
    });
    return Object.values(pg);
  };

  const getDateStats = (date) => {
    const dayData = groupedData[date] || [];
    const dayExp = groupedExpenses[date] || [];
    const totalSalary = dayData.reduce(
      (s, w) => s + (parseFloat(w.salary) || 0),
      0,
    );
    const totalExpenses = dayExp.reduce(
      (s, e) => s + (parseFloat(e.price) || 0),
      0,
    );
    return {
      totalSalary,
      totalExpenses,
      employeeCount: new Set(dayData.map((w) => w.employeeId)).size,
      placeCount: new Set(dayData.map((w) => w.placeId)).size,
      workCount: dayData.length,
      expenseCount: dayExp.length,
      hasActivity: dayData.length > 0 || dayExp.length > 0,
    };
  };

  const hasNotes = (date) =>
    (groupedData[date] || []).some((i) => i.note?.trim());

  const getMonthTotals = () => {
    const allWork = Object.values(groupedData).flat();
    const allExp = Object.values(groupedExpenses).flat();
    const totalDailySalaries = allWork.reduce(
      (s, w) => s + (parseFloat(w.salary) || 0),
      0,
    );
    const monthlyEmps = Array.isArray(allData?.myMonthlySalary)
      ? allData.myMonthlySalary.filter((i) => {
          if (!i.date) return false;
          const [, m, y] = i.date.split("-");
          return `${m}-${y}` === choosedMonth;
        })
      : [];
    const totalMonthlySalaries = monthlyEmps.reduce((s, item) => {
      const emp = (allData.myEmployees || []).find(
        (e) => e.id === item.employeeId,
      );
      return s + (emp?.monthlySalary || 0);
    }, 0);
    const totalSalaries = totalDailySalaries + totalMonthlySalaries;
    const totalExpenses = allExp.reduce(
      (s, e) => s + (parseFloat(e.price) || 0),
      0,
    );
    const totalExtraProfit = Array.isArray(allData?.myPlaceExtraProfit)
      ? allData.myPlaceExtraProfit
          .filter((i) => {
            if (!i.date) return false;
            const [, m, y] = i.date.split("-");
            return `${m}-${y}` === choosedMonth;
          })
          .reduce((s, i) => s + (parseFloat(i.profit) || 0), 0)
      : 0;
    const uniquePlaceIds = new Set(allWork.map((w) => w.placeId));
    let dailyRevenue = 0,
      monthlyRevenue = 0,
      globalRevenue = 0,
      dailyPlaces = 0,
      monthlyPlaces = 0,
      globalPlaces = 0;
    uniquePlaceIds.forEach((placeId) => {
      const place = (allData.myPlaces || []).find((p) => p.id === placeId);
      if (!place) return;
      if (place.paidWay === 2) {
        dailyRevenue += allWork
          .filter((w) => w.placeId === placeId)
          .reduce((s, w) => s + (parseFloat(w.placeDailySalary) || 0), 0);
        dailyPlaces++;
      } else if (place.paidWay === 3) {
        globalPlaces++;
        if (Array.isArray(allData?.myPlacesGloballyPaid))
          allData.myPlacesGloballyPaid
            .filter((p) => {
              if (p.placeId !== placeId || !p.dateGetPaid) return false;
              const [, m, y] = p.dateGetPaid.split("-");
              return `${m}-${y}` === choosedMonth;
            })
            .forEach((p) => {
              globalRevenue += parseFloat(p.profit || 0);
            });
      } else {
        monthlyRevenue += parseFloat(place.profit || 0);
        monthlyPlaces++;
      }
    });
    const regularRevenue = dailyRevenue + monthlyRevenue;
    const totalRevenue = regularRevenue + globalRevenue + totalExtraProfit;
    const totalCosts = totalSalaries + totalExpenses + totalInvoicesCost;
    return {
      totalSalaries,
      totalExpenses,
      totalInvoicesCost,
      dailyRevenue,
      monthlyRevenue,
      regularRevenue,
      globalRevenue,
      totalExtraProfit,
      totalRevenue,
      totalCosts,
      dailyPlaces,
      monthlyPlaces,
      regularPlaces: dailyPlaces + monthlyPlaces,
      globalPlaces,
      totalPlaces: uniquePlaceIds.size,
      totalNetProfit: totalRevenue - totalCosts,
      totalEmployees: new Set(allWork.map((w) => w.employeeId)).size,
      workingDays: Object.keys(groupedData).length,
    };
  };
  const mt = getMonthTotals();

  const handleCardPress = (type) => {
    const allWork = Object.values(groupedData).flat();
    let data = [];
    if (type === "activeDays") {
      data = Object.keys(groupedData).sort((a, b) =>
        moment(b, "DD-MM-YYYY").diff(moment(a, "DD-MM-YYYY")),
      );
    } else if (type === "employees") {
      const map = new Map();
      allWork.forEach((w) => {
        const emp = (allData.myEmployees || []).find(
          (e) => e.id === w.employeeId,
        );
        if (emp) {
          if (!map.has(emp.id)) map.set(emp.id, { ...emp, workDays: 0 });
          map.get(emp.id).workDays++;
        }
      });
      data = Array.from(map.values());
    } else if (type === "places") {
      const map = new Map();
      allWork.forEach((w) => {
        const pl = (allData.myPlaces || []).find((p) => p.id === w.placeId);
        if (pl) {
          if (!map.has(pl.id)) map.set(pl.id, { ...pl, workCount: 0 });
          map.get(pl.id).workCount++;
        }
      });
      data = Array.from(map.values());
    }
    setModalData(data);
    setModalType(type);
    setModalVisible(true);
  };

  const handleDatePress = (date) => {
    localStorage.setItem("choosedDate", date);
    history.push("/boss-statisticby-date-data");
  };

  const getGlobalPaymentsByPlace = () => {
    const allG = Array.isArray(allData?.myPlacesGloballyPaid)
      ? allData.myPlacesGloballyPaid
      : [];
    const byPlace = {};
    allG.forEach((p) => {
      if (!byPlace[p.placeId]) {
        const place = (allData.myPlaces || []).find(
          (pl) => pl.id === p.placeId,
        );
        byPlace[p.placeId] = {
          placeId: p.placeId,
          placeName: place?.place || "Unknown",
          totalContract: parseFloat(place?.profit || 0),
          payments: [],
          received: 0,
        };
      }
      byPlace[p.placeId].payments.push(p);
      byPlace[p.placeId].received += parseFloat(p.profit || 0);
    });
    return Object.values(byPlace);
  };

  const getExtraProfitsByPlace = () => {
    const items = Array.isArray(allData?.myPlaceExtraProfit)
      ? allData.myPlaceExtraProfit.filter((i) => {
          if (!i.date) return false;
          const [, m, y] = i.date.split("-");
          return `${m}-${y}` === choosedMonth;
        })
      : [];
    const byPlace = {};
    items.forEach((i) => {
      const place = (allData.myPlaces || []).find((p) => p.id === i.placeId);
      const name = place?.place || "Unknown";
      if (!byPlace[i.placeId])
        byPlace[i.placeId] = { placeName: name, items: [], total: 0 };
      byPlace[i.placeId].items.push(i);
      byPlace[i.placeId].total += parseFloat(i.profit || 0);
    });
    return Object.values(byPlace);
  };

  const globalPaymentsByPlace = getGlobalPaymentsByPlace();
  const extraProfitsByPlace = getExtraProfitsByPlace();

  return (
    <div className="sbd">
      <style>{STYLES}</style>

      {/* Month scroller */}
      <div className="sbd-month-section">
        <p className="sbd-sec-label">{t("selectMonth")}</p>
        <div className="sbd-month-row">
          <button className="sbd-month-arrow" onClick={() => scrollMonths(-1)}>
            <ChevronLeft size={14} />
          </button>
          <div className="sbd-month-scroll-wrap">
            <div className="sbd-month-track" ref={monthTrackRef}>
              {monthList.map((item) => {
                const [, year] = item.split("-");
                return (
                  <button
                    key={item}
                    className={`sbd-month-btn${choosedMonth === item ? " active" : ""}`}
                    onClick={() => setChoosedMonth(item)}
                  >
                    <span className="sbd-month-name">
                      {moment(item, "MM-YYYY").format("MMM")}
                    </span>
                    <span className="sbd-month-year">{year}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <button className="sbd-month-arrow" onClick={() => scrollMonths(1)}>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {choosedMonth && (
        <>
          {/* Page title */}
          <p className="sbd-page-title">
            <span>{moment(choosedMonth, "MM-YYYY").format("MMMM YYYY")}</span>{" "}
            {t("summary")}
          </p>

          {/* Quick stats */}
          <div className="sbd-stat-row">
            {[
              {
                type: "activeDays",
                val: mt.workingDays,
                lbl: t("workingDays"),
                cls: "blue",
                ico: <Calendar size={15} color="#3B82F6" />,
              },
              {
                type: "employees",
                val: mt.totalEmployees,
                lbl: t("employees"),
                cls: "purple",
                ico: <Users size={15} color="#7C3AED" />,
              },
              {
                type: "places",
                val: mt.totalPlaces,
                lbl: t("place"),
                cls: "amber",
                ico: <MapPin size={15} color="#F59E0B" />,
              },
            ].map(({ type, val, lbl, cls, ico }) => (
              <button
                key={type}
                className={`sbd-stat-btn ${cls}`}
                onClick={() => handleCardPress(type)}
              >
                <div className={`sbd-stat-ico ${cls}`}>{ico}</div>
                <div className="sbd-stat-val">{val}</div>
                <div className="sbd-stat-lbl">{lbl}</div>
              </button>
            ))}
          </div>

          {/* Regular Operations */}
          <div className="sbd-block">
            <div className="sbd-block-header">
              <div className="sbd-block-header-left">
                <div
                  className="sbd-block-ico"
                  style={{ background: "#EEF2FF" }}
                >
                  <Building size={15} color="#4F46E5" />
                </div>
                <span className="sbd-block-title">
                  {t("regularOperations")}
                </span>
              </div>
              <span
                className="sbd-block-badge"
                style={{ background: "#EEF2FF", color: "#4F46E5" }}
              >
                {mt.regularPlaces} places
              </span>
            </div>
            <div className="sbd-rev-grid">
              <div
                className="sbd-rev-card"
                style={{ borderLeftColor: "#2DD4BF" }}
              >
                <div className="sbd-rev-card-lbl">
                  <Calendar size={11} color="#0D9488" /> {t("daily")}
                  <InfoTooltip text="Total sum of profits from all places with daily payment this month" />
                </div>
                <div className="sbd-rev-card-val">
                  ₪{mt.dailyRevenue.toLocaleString()}
                </div>
                <div className="sbd-rev-card-sub">{mt.dailyPlaces} places</div>
              </div>
              <div
                className="sbd-rev-card"
                style={{ borderLeftColor: "#818CF8" }}
              >
                <div className="sbd-rev-card-lbl">
                  <Calendar size={11} color="#6366F1" /> {t("monthly")}
                  <InfoTooltip text="Total sum of profits from all places with monthly payment this month" />
                </div>
                <div className="sbd-rev-card-val">
                  ₪{mt.monthlyRevenue.toLocaleString()}
                </div>
                <div className="sbd-rev-card-sub">
                  {mt.monthlyPlaces} places
                </div>
              </div>
            </div>
            <div className="sbd-total-row">
              <div className="sbd-total-row-left">
                <div
                  className="sbd-block-ico"
                  style={{
                    background: "#D1FAE5",
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                  }}
                >
                  <TrendingUp size={13} color="#059669" />
                </div>
                <span className="sbd-total-lbl">{t("totalRevenue")}</span>
              </div>
              <span className="sbd-total-val" style={{ color: "#059669" }}>
                ₪{mt.regularRevenue.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Global Contracts */}
          {mt.globalPlaces > 0 && (
            <div className="sbd-block">
              <div className="sbd-block-header">
                <div className="sbd-block-header-left">
                  <div
                    className="sbd-block-ico"
                    style={{ background: "#EDE9FE" }}
                  >
                    <Globe size={15} color="#7C3AED" />
                  </div>
                  <span className="sbd-block-title" style={{ display: "inline-flex", alignItems: "center" }}>
                    {t("globalContracts")}
                    <InfoTooltip text="Total payments received from all global contract places across all locations" />
                  </span>
                </div>
                <span
                  className="sbd-block-badge"
                  style={{ background: "#EDE9FE", color: "#5B21B6" }}
                >
                  {mt.globalPlaces} contracts
                </span>
              </div>
              <div className="sbd-total-row" style={{ marginBottom: 12 }}>
                <div className="sbd-total-row-left">
                  <div
                    className="sbd-block-ico"
                    style={{
                      background: "#EDE9FE",
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                    }}
                  >
                    <DollarSign size={13} color="#7C3AED" />
                  </div>
                  <span className="sbd-total-lbl">{t("totalRevenue")}</span>
                </div>
                <span className="sbd-total-val" style={{ color: "#7C3AED" }}>
                  ₪{mt.globalRevenue.toLocaleString()}
                </span>
              </div>
              {globalPaymentsByPlace.map((pg, i) => {
                const pct =
                  pg.totalContract > 0
                    ? Math.min((pg.received / pg.totalContract) * 100, 100)
                    : 0;
                return (
                  <div key={i} className="sbd-contract-card">
                    <div className="sbd-contract-header">
                      <div className="sbd-contract-dot" />
                      <span className="sbd-contract-name">{pg.placeName}</span>
                      <span className="sbd-contract-badge">
                        {pg.payments.length} pmts
                      </span>
                    </div>
                    <div className="sbd-contract-amounts">
                      <span className="sbd-contract-recv">
                        ₪{pg.received.toLocaleString()}
                      </span>
                      <span className="sbd-contract-slash">/</span>
                      <span className="sbd-contract-total">
                        ₪{pg.totalContract.toLocaleString()}
                      </span>
                      <span className="sbd-contract-pct">
                        {pct.toFixed(0)}%
                      </span>
                    </div>
                    <div className="sbd-progress">
                      <div
                        className="sbd-progress-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="sbd-contract-payments">
                      {pg.payments.map((p, j) => (
                        <div key={j} className="sbd-payment-row">
                          <span className="sbd-payment-date">
                            <Calendar size={10} />
                            {moment(p.dateGetPaid, "DD-MM-YYYY").format(
                              "MMM DD, YYYY",
                            )}
                          </span>
                          <span className="sbd-payment-amt">
                            ₪{parseFloat(p.profit).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                    {pg.totalContract > pg.received ? (
                      <div className="sbd-status-chip bad">
                        <AlertCircle size={11} />
                        Remaining: ₪
                        {(pg.totalContract - pg.received).toLocaleString()}
                      </div>
                    ) : pg.totalContract > 0 ? (
                      <div className="sbd-status-chip ok">
                        <CheckCircle size={11} />
                        Fully Paid
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          {/* Extra Profit */}
          {mt.totalExtraProfit > 0 && (
            <div className="sbd-block">
              <div className="sbd-block-header">
                <div className="sbd-block-header-left">
                  <div
                    className="sbd-block-ico"
                    style={{ background: "#FEF3C7" }}
                  >
                    <Gift size={15} color="#D97706" />
                  </div>
                  <span className="sbd-block-title">{t("extraProfit")}</span>
                </div>
                <span
                  className="sbd-block-badge"
                  style={{ background: "#FEF3C7", color: "#92400E" }}
                >
                  ₪{mt.totalExtraProfit.toLocaleString()}
                </span>
              </div>
              {extraProfitsByPlace.map((pg, i) => (
                <div key={i} className="sbd-extra-card">
                  <div className="sbd-extra-header">
                    <div className="sbd-extra-dot" />
                    <span className="sbd-extra-name">{pg.placeName}</span>
                    <span className="sbd-extra-total">
                      ₪{pg.total.toLocaleString()}
                    </span>
                  </div>
                  {pg.items.map((item, j) => (
                    <div key={j} className="sbd-extra-row">
                      <div className="sbd-extra-left">
                        <span className="sbd-extra-date">
                          {moment(item.date, "DD-MM-YYYY").format("MMM DD")}
                        </span>
                        {item.note?.trim() && (
                          <span className="sbd-extra-note">• {item.note}</span>
                        )}
                      </div>
                      <span className="sbd-extra-amt">
                        ₪{parseFloat(item.profit).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Operating Costs */}
          <div className="sbd-block">
            <div className="sbd-block-header">
              <div className="sbd-block-header-left">
                <div
                  className="sbd-block-ico"
                  style={{ background: "#FEE2E2" }}
                >
                  <Calculator size={15} color="#DC2626" />
                </div>
                <span className="sbd-block-title">{t("operatingCosts")}</span>
              </div>
              <span
                className="sbd-block-badge"
                style={{ background: "#FEE2E2", color: "#991B1B" }}
              >
                ₪{mt.totalCosts.toLocaleString()}
              </span>
            </div>

            {/* Salaries */}
            <div className="sbd-cost-row">
              <div className="sbd-cost-ico" style={{ background: "#FEE2E2" }}>
                <DollarSign size={14} color="#DC2626" />
              </div>
              <span className="sbd-cost-lbl">{t("totalSalaries")}</span>
              <span className="sbd-cost-val" style={{ color: "#DC2626" }}>
                ₪{mt.totalSalaries.toLocaleString()}
              </span>
            </div>

            {/* Expenses */}
            <button
              className="sbd-cost-row"
              style={{
                width: "100%",
                cursor: "pointer",
                border: "none",
                textAlign: "left",
                transition: "all 0.15s",
              }}
              onClick={() => history.push("/boss-statistic-create-expenses")}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#FEF3C7")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#F9FAFB")
              }
            >
              <div className="sbd-cost-ico" style={{ background: "#FEF3C7" }}>
                <Receipt size={14} color="#D97706" />
              </div>
              <span className="sbd-cost-lbl">{t("totalExpenses")}</span>
              <span className="sbd-cost-val" style={{ color: "#D97706" }}>
                ₪{mt.totalExpenses.toLocaleString()}
              </span>
              <ChevronRight
                size={14}
                color="#D97706"
                style={{ marginLeft: 4 }}
              />
            </button>

            {/* ── Invoice Costs ── */}
            <div
              className="sbd-cost-row"
              style={{ flexDirection: "column", alignItems: "stretch", gap: 0 }}
            >
              {/* Header row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: monthInvoices.length > 0 ? 10 : 0,
                }}
              >
                <div className="sbd-cost-ico" style={{ background: "#EDE9FE" }}>
                  <FileText size={14} color="#7C3AED" />
                </div>
                <span className="sbd-cost-lbl">{t("invoiceCosts")}</span>
                <span
                  style={{
                    marginLeft: "auto",
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 14,
                    fontWeight: 800,
                    color: "#7C3AED",
                  }}
                >
                  {totalInvoicesCost > 0
                    ? `₪${totalInvoicesCost.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : "—"}
                </span>
              </div>

              {/* Invoice list — grouped by place */}
              {monthInvoices.length === 0 ? (
                <p
                  style={{
                    fontSize: 11,
                    color: "#9CA3AF",
                    textAlign: "center",
                    padding: "4px 0",
                  }}
                >
                  No invoices this month
                </p>
              ) : (
                (() => {
                  const invoicesByPlace = monthInvoices.reduce((acc, inv) => {
                    const pid = inv.placeId;
                    if (!acc[pid]) {
                      const pl = (allData.myPlaces || []).find(
                        (p) => p.id === pid,
                      );
                      acc[pid] = {
                        placeName: pl?.place || `Place ${pid}`,
                        invoices: [],
                        total: 0,
                      };
                    }
                    acc[pid].invoices.push(inv);
                    acc[pid].total +=
                      inv.ilsEquivalent != null
                        ? inv.ilsEquivalent
                        : parseFloat(inv.price || 0);
                    return acc;
                  }, {});
                  const placeGroups = Object.values(invoicesByPlace);
                  return (
                    <>
                      {placeGroups.map((pg, gi) => (
                        <div
                          key={gi}
                          className="sbd-contract-card"
                          style={{ marginTop: gi === 0 ? 4 : 8 }}
                        >
                          <div className="sbd-contract-header">
                            <div className="sbd-contract-dot" />
                            <span className="sbd-contract-name">
                              {pg.placeName}
                            </span>
                            <span className="sbd-contract-badge">
                              {pg.invoices.length} inv
                            </span>
                          </div>
                          {pg.invoices.map((inv) => {
                            const currency = inv.currency || "ILS";
                            const sym = CURRENCY_SYM[currency] || currency;
                            const faceValue = parseFloat(inv.price || 0);
                            const ilsVal = inv.ilsEquivalent;
                            const isForeign = currency !== "ILS";
                            const rateAvailable = inv.exchangeRate != null;
                            return (
                              <div
                                key={inv.id}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  fontSize: 11,
                                  color: "#6b7280",
                                  padding: "8px 0",
                                  borderBottom: "1px solid #f9fafb",
                                  gap: 8,
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      color: "#374151",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {inv.subject ||
                                      inv.whoSend ||
                                      inv.description ||
                                      "Invoice"}
                                  </div>
                                  {inv.date && (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: "#9ca3af",
                                        marginTop: 1,
                                      }}
                                    >
                                      {inv.date}
                                    </div>
                                  )}
                                  {isForeign && (
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: "#7c3aed",
                                        marginTop: 2,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {rateAvailable
                                        ? `1 ${currency} = ₪${inv.exchangeRate.toFixed(3)}`
                                        : "Rate unavailable — using face value"}
                                    </div>
                                  )}
                                </div>
                                <div
                                  style={{ textAlign: "right", flexShrink: 0 }}
                                >
                                  <div
                                    style={{
                                      fontWeight: 700,
                                      color: "#7c3aed",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {sym}
                                    {faceValue.toLocaleString("he-IL", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                    {isForeign && (
                                      <span
                                        style={{
                                          fontSize: 9,
                                          marginLeft: 3,
                                          color: "#9ca3af",
                                        }}
                                      >
                                        {currency}
                                      </span>
                                    )}
                                  </div>
                                  {isForeign && ilsVal != null && (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: "#6d28d9",
                                        fontFamily: "monospace",
                                        marginTop: 2,
                                      }}
                                    >
                                      ≈ ₪
                                      {ilsVal.toLocaleString("he-IL", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </div>
                                  )}
                                  {isForeign && ilsVal == null && (
                                    <div
                                      style={{
                                        fontSize: 9,
                                        color: "#f59e0b",
                                        marginTop: 2,
                                      }}
                                    >
                                      (rate lookup failed)
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginTop: 8,
                              paddingTop: 8,
                              borderTop: "1.5px solid #ede9fe",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#6d28d9",
                              }}
                            >
                              Total (ILS equivalent)
                            </span>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 800,
                                color: "#7c3aed",
                                fontFamily: "monospace",
                              }}
                            >
                              ₪
                              {pg.total.toLocaleString("he-IL", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        </div>
                      ))}
                      {placeGroups.length > 1 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            paddingTop: 8,
                            marginTop: 6,
                            borderTop: "1.5px solid #ede9fe",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#6d28d9",
                            }}
                          >
                            Total (ILS)
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 800,
                              color: "#7c3aed",
                              fontFamily: "monospace",
                            }}
                          >
                            ₪
                            {totalInvoicesCost.toLocaleString("he-IL", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>

          {/* Total Summary */}
          <div className="sbd-summary-block">
            <div className="sbd-summary-title">
              <div
                className="sbd-block-ico"
                style={{
                  background: "#F3F4F6",
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                }}
              >
                <Calculator size={13} color="#374151" />
              </div>
              {t("totalSummary")}
            </div>
            <div className="sbd-breakdown-box">
              <div className="sbd-breakdown-total">
                <span className="sbd-breakdown-total-lbl">
                  {t("totalRevenue")}
                </span>
                <span className="sbd-breakdown-total-val">
                  ₪{mt.totalRevenue.toLocaleString()}
                </span>
              </div>
              <div className="sbd-breakdown-line">
                <span className="sbd-breakdown-line-lbl">
                  {t("regularOperations")}
                </span>
                <span className="sbd-breakdown-line-val">
                  ₪{mt.regularRevenue.toLocaleString()}
                </span>
              </div>
              {mt.globalPlaces > 0 && (
                <div className="sbd-breakdown-line">
                  <span className="sbd-breakdown-line-lbl">
                    {t("globalContracts")}
                  </span>
                  <span className="sbd-breakdown-line-val">
                    ₪{mt.globalRevenue.toLocaleString()}
                  </span>
                </div>
              )}
              {mt.totalExtraProfit > 0 && (
                <div className="sbd-breakdown-line">
                  <span className="sbd-breakdown-line-lbl">
                    {t("extraProfit")}
                  </span>
                  <span className="sbd-breakdown-line-val">
                    ₪{mt.totalExtraProfit.toLocaleString()}
                  </span>
                </div>
              )}
              {/* Cost breakdown */}
              <div
                style={{ height: 1, background: "#E5E7EB", margin: "8px 0" }}
              />
              <div className="sbd-breakdown-line">
                <span
                  className="sbd-breakdown-line-lbl"
                  style={{ color: "#DC2626" }}
                >
                  − {t("totalSalaries")}
                </span>
                <span
                  className="sbd-breakdown-line-val"
                  style={{ color: "#DC2626" }}
                >
                  ₪{mt.totalSalaries.toLocaleString()}
                </span>
              </div>
              <div className="sbd-breakdown-line">
                <span
                  className="sbd-breakdown-line-lbl"
                  style={{ color: "#D97706" }}
                >
                  − {t("totalExpenses")}
                </span>
                <span
                  className="sbd-breakdown-line-val"
                  style={{ color: "#D97706" }}
                >
                  ₪{mt.totalExpenses.toLocaleString()}
                </span>
              </div>
              {totalInvoicesCost > 0 && (
                <div className="sbd-breakdown-line">
                  <span
                    className="sbd-breakdown-line-lbl"
                    style={{ color: "#7C3AED" }}
                  >
                    − {t("invoices")}
                  </span>
                  <span
                    className="sbd-breakdown-line-val"
                    style={{ color: "#7C3AED" }}
                  >
                    ₪
                    {totalInvoicesCost.toLocaleString("he-IL", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              )}
            </div>
            <div
              className={`sbd-net ${mt.totalNetProfit >= 0 ? "positive" : "negative"}`}
            >
              <div>
                <div className="sbd-net-lbl">{t("netProfit")}</div>
                <div className="sbd-net-val">
                  ₪{mt.totalNetProfit.toLocaleString()}
                </div>
              </div>
              <div className="sbd-net-ico">
                {mt.totalNetProfit >= 0 ? (
                  <CheckCircle size={22} color="#059669" />
                ) : (
                  <XCircle size={22} color="#DC2626" />
                )}
              </div>
            </div>
          </div>

          {/* Daily Breakdown */}
          <div>
            <div className="sbd-daily-header">
              <p className="sbd-sec-label" style={{ marginBottom: 0 }}>
                {t("dailyBreakdown")}
              </p>
              <span className="sbd-daily-badge">{allDays.length} days</span>
            </div>
            {allDays.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#9CA3AF",
                  fontSize: 13,
                }}
              >
                Select a month to view daily data
              </div>
            ) : (
              <div className="sbd-day-grid">
                {allDays.map((date) => {
                  const stats = getDateStats(date);
                  const hasNote = hasNotes(date);
                  const isToday = moment(date, "DD-MM-YYYY").isSame(
                    moment(),
                    "day",
                  );
                  const placeGroups = getDateWorkByPlace(date);
                  const dayNum = moment(date, "DD-MM-YYYY").format("DD");
                  const dayName = moment(date, "DD-MM-YYYY").format("ddd");
                  return (
                    <button
                      key={date}
                      className={`sbd-day${stats.hasActivity ? " active" : ""}${isToday ? " today" : ""}`}
                      onClick={() => handleDatePress(date)}
                    >
                      {hasNote && (
                        <div className="sbd-day-note-dot">
                          <FileText size={8} color="#D97706" />
                        </div>
                      )}
                      <div className="sbd-day-top">
                        <span className="sbd-day-num">{dayNum}</span>
                        <span className="sbd-day-name">{dayName}</span>
                      </div>
                      {stats.hasActivity ? (
                        <>
                          <div className="sbd-day-badges">
                            {stats.workCount > 0 && (
                              <>
                                <span className="sbd-chip emp">
                                  <Users size={8} />
                                  {stats.employeeCount}
                                </span>
                                <span className="sbd-chip map">
                                  <MapPin size={8} />
                                  {stats.placeCount}
                                </span>
                              </>
                            )}
                            {stats.expenseCount > 0 && (
                              <span className="sbd-chip exp">
                                <Receipt size={8} />
                                {stats.expenseCount}
                              </span>
                            )}
                          </div>
                          <div className="sbd-day-places">
                            {placeGroups.map((pg, pi) => (
                              <div key={pi} className="sbd-place-row">
                                <div className="sbd-place-name-row">
                                  <div className="sbd-place-dot" />
                                  <span className="sbd-place-name-txt">
                                    {pg.placeName}
                                  </span>
                                  <span className="sbd-place-emp-count">
                                    {pg.employees.length}
                                  </span>
                                </div>
                                {pg.employees.map((emp, ei) => (
                                  <div key={ei} className="sbd-emp-line">
                                    <span className="sbd-emp-line-name">
                                      • {emp.name}
                                    </span>
                                    <span className="sbd-emp-line-sal">
                                      ₪{emp.salary.toLocaleString()}
                                    </span>
                                  </div>
                                ))}
                                <div className="sbd-place-total-row">
                                  <span className="sbd-place-total-lbl">
                                    {t("total")}
                                  </span>
                                  <span className="sbd-place-total-val">
                                    ₪{pg.totalSalary.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            ))}
                            <div className="sbd-day-totals">
                              <div className="sbd-day-total-line">
                                <span className="sbd-day-total-lbl">
                                  <DollarSign size={9} color="#6B7280" />
                                  {t("totalSalaries")}
                                </span>
                                <span className="sbd-day-total-val">
                                  ₪{stats.totalSalary.toLocaleString()}
                                </span>
                              </div>
                              {stats.expenseCount > 0 && (
                                <div
                                  className="sbd-day-total-line"
                                  style={{ marginTop: 3 }}
                                >
                                  <span className="sbd-day-total-lbl">
                                    <Receipt size={9} color="#D97706" />
                                    {t("totalExpenses")}
                                  </span>
                                  <span
                                    className="sbd-day-total-val"
                                    style={{ color: "#D97706" }}
                                  >
                                    ₪{stats.totalExpenses.toLocaleString()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="sbd-day-empty">
                          <span>{t("noActivity")}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      <BossStatisticByDateShowDataModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        type={modalType}
        data={modalData}
        monthName={
          choosedMonth
            ? moment(choosedMonth, "MM-YYYY").format("MMMM YYYY")
            : ""
        }
      />
    </div>
  );
}
